import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertModule,
  ButtonsModule,
  CardModule,
  RadioButtonModule,
  LoadingService,
} from 'axis-components';
import { OutstandingStateSummaryService } from '../../../_services/pip/outstanding-state-summary.service';
import { RequestService } from '../../../_services/request/request.service';
import { FormType, RequestProgressStep } from '../../../config/constants';
import { PipBaseRulesService } from '../../../state-disclosures/pip/services/pip-base-rules.service';
import { timer, Subscription } from 'rxjs';
import { PipBaseStateComponent } from '../../../state-disclosures/pip/pip-base-state.component';
import { BrowserNavigationService } from '../../../_services/browser-navigation.service';
import { HeaderSharedService } from '../../../_services/header-shared.service';
@Component({
  selector: 'app-pip-outstanding-state-summary',
  templateUrl: './pip-outstanding-state-summary.component.html',
  styleUrls: ['./pip-outstanding-state-summary.component.scss'],
  standalone: true,
  imports: [
    ButtonsModule,
    CardModule,
    RadioButtonModule,
    ReactiveFormsModule,
    AlertModule,
  ],
})
export class PipOutstandingStateSummaryComponent {
  reactiveForm: FormGroup;
  public baseRulesService = inject(PipBaseRulesService);
  @ViewChild('outstandingContainer')
  outstandingContainer?: ElementRef<HTMLElement>;
  @ViewChild('stateComponent', { read: ViewContainerRef })
  stateComponent!: ViewContainerRef;
  showValidationError = false;
  private stateComponentRefs: any[] = [];
  private subscription = new Subscription();

  // Events emitted to playground component for workflow navigation
  @Output() continueClickedPlayground = new EventEmitter<void>();
  @Output() backClickedPlayground = new EventEmitter<void>();

  constructor(
    private readonly outstandingStateSummaryService: OutstandingStateSummaryService,
    private router: Router,
    private fb: FormBuilder,
    private requestService: RequestService,
    private loadingService: LoadingService,
    private browserNavigationService: BrowserNavigationService,
    private headerSharedService: HeaderSharedService,
  ) {
    this.reactiveForm = this.fb.group({
      states: this.fb.array([]),
    });
  }

  ngAfterViewInit(): void {
    this.setupControls();
  }
  get statesFormArray(): FormArray {
    return this.reactiveForm.get('states') as FormArray;
  }

  clearAllFields(): void {
    this.statesFormArray.controls.forEach((formGroup: FormGroup) => {
      Object.values(formGroup.controls).forEach((control) => {
        if (control instanceof FormArray) {
          control.clear();
        } else if (control instanceof FormGroup) {
          Object.values(control.controls).forEach((nestedControl) => {
            nestedControl.setValue('');
            nestedControl.clearValidators();
            nestedControl.markAsUntouched();
            nestedControl.updateValueAndValidity();
          });
        } else {
          control.setValue('');
          control.clearValidators();
          control.markAsUntouched();
          control.updateValueAndValidity();
        }
      });
      formGroup.markAsUntouched();
      formGroup.updateValueAndValidity();
    });
  }

  getStateFormGroup(index: number) {
    return this.statesFormArray.at(index) as FormGroup;
  }

  ngOnInit(): void {
    this.browserNavigationService.startListening({
      onBackNavigation: () => this.onBrowserback(),
    });
    this.subscription.add(
      this.baseRulesService.saveData$.subscribe((value) => {
        if (value.saveDataDump) {
          this.setDataDump();
        }
      }),
    );
    this.goTop();
  }

  onBrowserback(): void {
    this.setDataDump();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.browserNavigationService.stopListening();
  }
  private setupControls() {
    this.loadingService.start('Outstanding State Selections is Loading');
    const promises = [];
    let applicableStates = this.baseRulesService.getApplicableStates();
    PipBaseStateComponent.resetLoadingState();
    applicableStates.forEach(({ state, stateComponent }, stateIndex) => {
      promises.push(
        new Promise((resolve) =>
          this.baseRulesService.getAllRules(state).subscribe((allRules) => {
            const controlObj = {};
            const outstandingStates =
              this.baseRulesService.dataDump['outstandingStates'];
            let stateData = {};
            if (outstandingStates && outstandingStates[stateIndex]) {
              const stateItem = outstandingStates[stateIndex];
              stateData = stateItem.data || {};
            } else {
              this.clearAllFields();
            }
            allRules.controls.forEach((control) => {
              const savedValue = stateData[control.formControlName];
              if (
                savedValue !== undefined &&
                savedValue !== null &&
                savedValue !== ''
              ) {
                controlObj[control.formControlName] = [
                  savedValue,
                  Validators.required,
                ];
              } else if (
                control.formControlName.endsWith('_DropDown') ||
                control.formControlName.endsWith('_Input') ||
                control.formControlName.endsWith('_Checkbox')
              ) {
                controlObj[control.formControlName] = [''];
              } else if (control.formControlName.endsWith('_Radio')) {
                controlObj[control.formControlName] = [null];
              }
            });
            const formGroup = this.fb.group(controlObj);
            applicableStates[stateIndex].formGroup = formGroup;
            resolve('');
          }),
        ),
      );
    });
    // Use timer to check every 10ms and unsubscribe if all formGroups are set
    // this is temporary solution until we get all the json compiled into 1 json for all states from backend
    Promise.all(promises).then((val) => {
      // const sub = timer(0, 10).subscribe(() => {
      // if (applicableStates.every((s) => s.formGroup != undefined)) {
      // sub.unsubscribe();
      applicableStates.forEach(
        ({ state, stateComponent, formGroup }, stateIndex) => {
          this.statesFormArray.push(formGroup);
          const stateComponentRef =
            this.stateComponent.createComponent(stateComponent);
          this.stateComponentRefs.push(stateComponentRef.instance);
          (stateComponentRef.instance as any).outstandingLoadingService =
            this.loadingService;
          (stateComponentRef.instance as any).formGroup =
            formGroup as FormGroup;
          (stateComponentRef.instance as any).showValidationError =
            this.showValidationError;
          (
            stateComponentRef.location.nativeElement as HTMLElement
          ).classList.add('state-wrapper');
        },
      );

      // Subscribe to form changes to reset subsequent steps (AC2)
      this.reactiveForm.valueChanges.subscribe(() => {
        this.headerSharedService.resetSteps('pip');
      });
    });
  }

  get isStatePageEmptyScoped(): boolean {
    if (this.outstandingContainer?.nativeElement) {
      return this.baseRulesService.isStatePageEmpty(
        this.outstandingContainer.nativeElement,
      );
    }

    return this.baseRulesService.isStatePageEmpty();
  }

  goTop(): void {
    // reset the page when changing route
    window.scrollTo(0, 0);
  }

  onBack(): void {
    this.setDataDump();
    if (this.baseRulesService.isPlayground) {
      // In playground mode, emit event instead of navigating
      this.backClickedPlayground.emit();
      return;
    }
    this.router.navigate(['/insured/pip-review-state'], {
      queryParamsHandling: 'preserve',
    });
  }
  onContinue(): void {
    this.baseRulesService.isContinueButtonClicked = true;
    this.statesFormArray.controls.forEach((formGroup: FormGroup) => {
      Object.values(formGroup.controls).forEach((control) => {
        control.updateValueAndValidity();
      });
      formGroup.markAllAsTouched();
    });
    let isValid = false;
    if (this.requestService.getValidationKey() === false) {
      isValid = true;
    } else {
      isValid = this.reactiveForm.valid;
      if (!isValid) {
        const invalidControls = [];
        const applicableStates = this.baseRulesService.getApplicableStates();
        this.statesFormArray.controls.forEach(
          (formGroup: FormGroup, idx: number) => {
            const stateName = applicableStates[idx]?.state;
            Object.entries(formGroup.controls).forEach(([key, control]) => {
              if (control.invalid) {
                invalidControls.push({
                  state: stateName,
                  controlName: key,
                  errors: control.errors,
                });
              }
            });
          },
        );

        this.focusOnFirstInvalidControl(invalidControls);
      }
    }
    if (isValid) {
      this.setDataDump();
      console.log(this.baseRulesService.dataDump);
      if (this.baseRulesService.isPlayground) {
        // In playground mode, emit event instead of navigating
        this.continueClickedPlayground.emit();
        return;
      }

      this.requestService.refreshRequestData(false).subscribe({
        next: () => {
          this.handleContinueWithFreshStep();
        },
        error: () => {
          this.handleContinueWithFreshStep();
        },
      });
    } else {
      this.baseRulesService.isContinueButtonClicked = false;
    }
  }

  private handleContinueWithFreshStep(): void {
    // Read current DB-backed step snapshot before moving the form ahead.

    const isPipCompleted = this.requestService.isFormTypeAlreadyCompleted(
      FormType.PIP,
    );

    // If PIP is already completed, do not continue with consent routing.
    if (isPipCompleted) {
      return;
    }

    this.requestService.updateRequestProgress(
      FormType.PIP,
      RequestProgressStep.OUTSTANDING_STATE_SELECTIONS,
      this.baseRulesService.dataDump,
    );

    this.router.navigate(['/insured/pip-signature'], {
      queryParamsHandling: 'preserve',
    });
  }

  focusOnFirstInvalidControl(invalidControls: any[]): void {
    if (!invalidControls.length) return;
    console.log('Invalid controls:', invalidControls);
    const { state, controlName } = invalidControls[0];
    const stateSelector = `app-${state.toLowerCase()}`;
    const isDropdown = controlName.toLowerCase().includes('dropdown');
    const selector = isDropdown
      ? `axis-dropdown[id="${controlName}"]`
      : `input[name="${controlName}"],[formcontrolname="${controlName}"]`;

    const stateElement = document.querySelector(stateSelector) as HTMLElement;
    if (!stateElement) return;

    const el = stateElement.querySelector(selector) as HTMLElement;
    if (!el) return;

    const focusable = el.querySelector('input,button') as HTMLElement;
    (focusable || el).focus();
  }

  private setDataDump() {
    const outstandingStates = [];
    this.reactiveForm.controls['states'].value.forEach((state, index) => {
      let formData = {};
      for (const [key, value] of Object.entries(state)) {
        if (value) {
          formData[key] =
            typeof value === 'object'
              ? this.baseRulesService.getValueOfFormControls(value)
              : value;
        }
      }
      this.baseRulesService.getPdfDataDumpPropForOutstandingPage(
        this.baseRulesService.getApplicableStates()[index].state,
        formData,
      );
      formData = { data: formData };
      const stateObj = {
        ...formData,
        state: this.baseRulesService.getApplicableStates()[index].state,
      };
      outstandingStates.push(stateObj);
    });
    this.baseRulesService.dataDump = {
      ...this.baseRulesService.dataDump,
      ...{ outstandingStates: outstandingStates },
    };
    console.log('pdfdata', this.baseRulesService.pdfDataDump);
  }
}
